import { getEnv } from '../env';
import axios, { InternalAxiosRequestConfig } from 'axios';
import { msalInstance, scopes } from "../msalConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const httpClient = axios.create({
  baseURL: getEnv('REACT_APP_API_BASE_URL'),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track whether an interactive request is in progress
let isInteractionInProgress = false;

/**
 * Resolve the best available MSAL account.
 * Falls back to the first cached account if setActiveAccount() hasn't been
 * called yet (e.g. on hard page-load before MSAL finishes initializing).
 */
const getAccount = () => {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
};

/**
 * Acquire a fresh access token silently, falling back to a popup when the
 * silent flow requires user interaction.  Returns the access token string.
 * Throws if authentication ultimately fails.
 */
const acquireToken = async (forceRefresh = false): Promise<string> => {
  const account = getAccount();

  if (!account) {
    // No account at all — user has never logged in on this device/browser.
    throw new InteractionRequiredAuthError('no_account', 'No MSAL account found.');
  }

  try {
    const startTime = performance.now();
    const response = await msalInstance.acquireTokenSilent({ account, scopes, forceRefresh });
    console.log(`Token acquired silently in ${(performance.now() - startTime).toFixed(2)} ms`);

    // Log once on localhost for debugging
    const baseUrl = getEnv('REACT_APP_API_BASE_URL') || getEnv('REACT_APP_BASE_URL') || '';
    if (baseUrl.includes('localhost') && !(window as any).__authTokenLogged) {
      (window as any).__authTokenLogged = true;
      console.log('[DEV] Auth token:', response.accessToken);
    }

    return response.accessToken;
  } catch (silentError) {
    // Only escalate to interactive flow for interaction-required errors
    if (silentError instanceof InteractionRequiredAuthError) {
      if (isInteractionInProgress) {
        console.log('Authentication interaction already in progress — skipping duplicate popup.');
        throw new Error('Authentication already in progress');
      }

      console.log('Interaction required — attempting popup login…');
      isInteractionInProgress = true;

      try {
        const popupResponse = await msalInstance.acquireTokenPopup({ scopes, account });
        console.log('Popup login successful');
        return popupResponse.accessToken;
      } catch (popupError) {
        console.error('Popup login failed:', popupError);
        throw popupError; // let the callers decide what to do
      } finally {
        isInteractionInProgress = false;
      }
    }

    throw silentError;
  }
};

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token to every outgoing request
// ---------------------------------------------------------------------------
httpClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await acquireToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('Could not obtain access token for request:', err);
      // Save the route the user is currently on so we can restore it after login
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/') {
        sessionStorage.setItem('postLoginRedirect', currentPath);
      }
      window.dispatchEvent(new Event('msal:loginRequired'));
      throw err;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — retry once with a fresh token on 401, then give up
// ---------------------------------------------------------------------------
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; // prevent infinite retry loops

      try {
        console.log('Received 401 — attempting token refresh and retry…');
        const freshToken = await acquireToken(true);
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return httpClient(originalRequest); // retry original request
      } catch (refreshError) {
        // Refresh also failed — the user must re-authenticate interactively
        console.error('Token refresh failed after 401. Redirecting to login.', refreshError);
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/') {
          sessionStorage.setItem('postLoginRedirect', currentPath);
        }
        window.dispatchEvent(new Event('msal:loginRequired'));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default httpClient;
